import { reactive } from 'vue'

const MAX_SAMPLES = 5
const LOSS_WINDOW = 20
// Umbrales pensados para el RTT de red medido por el sondeo (endpoint mínimo,
// sin consultas de negocio). Una red normal queda en óptimo/aceptable; solo
// una latencia realmente alta degrada el indicador.
const OPTIMAL_MAX_MS = 600
const WARNING_MIN_MS = 2000
const POOR_MIN_MS = 4000
const ZERO_PERCENT_CEILING_MS = POOR_MIN_MS * 2
const OPTIMAL_PERCENTAGE_MIN = 70
const ACCEPTABLE_PERCENTAGE_MIN = 35
const WARNING_PERCENTAGE_MIN = 15
const LOSS_WARNING_RATE = 0.25
const LOSS_POOR_RATE = 0.5

export const NETWORK_QUALITY = {
  OPTIMAL: 'optimal',
  ACCEPTABLE: 'acceptable',
  WARNING: 'warning',
  POOR: 'poor',
  OFFLINE: 'offline',
  UNKNOWN: 'unknown'
}

// Máximo porcentaje coherente con cada etiqueta (límite superior de su tier).
// Si la pérdida degrada la etiqueta, el porcentaje mostrado se capa a este
// tope; sin esto la UI leería "Aceptable (95%)" o "Deficiente (95%)".
const TIER_MAX_PERCENTAGE = {
  [NETWORK_QUALITY.OPTIMAL]: 100,
  [NETWORK_QUALITY.ACCEPTABLE]: 69,
  [NETWORK_QUALITY.WARNING]: 34,
  [NETWORK_QUALITY.POOR]: 14
}

export const networkQualityState = reactive({
  samples: [],
  results: [],
  quality: NETWORK_QUALITY.UNKNOWN,
  averageMs: null,
  qualityPercentage: null,
  lossRate: 0,
  isOffline: false,
  lastSampleAt: null
})

const TIER_ORDER = [
  NETWORK_QUALITY.OPTIMAL,
  NETWORK_QUALITY.ACCEPTABLE,
  NETWORK_QUALITY.WARNING,
  NETWORK_QUALITY.POOR
]

const isCurrentlyOffline = () =>
  networkQualityState.isOffline || (typeof navigator !== 'undefined' && navigator.onLine === false)

export const computeQualityLabel = (percentage) => {
  if (percentage === null) {
    return NETWORK_QUALITY.UNKNOWN
  }

  if (percentage >= OPTIMAL_PERCENTAGE_MIN) {
    return NETWORK_QUALITY.OPTIMAL
  }

  if (percentage >= ACCEPTABLE_PERCENTAGE_MIN) {
    return NETWORK_QUALITY.ACCEPTABLE
  }

  if (percentage >= WARNING_PERCENTAGE_MIN) {
    return NETWORK_QUALITY.WARNING
  }

  return NETWORK_QUALITY.POOR
}

export const computeQualityPercentage = (averageMs) => {
  if (averageMs === null) {
    return null
  }

  if (averageMs <= OPTIMAL_MAX_MS) {
    // 0..OPTIMAL_MAX_MS -> 100..70
    return Math.round(100 - (averageMs / OPTIMAL_MAX_MS) * 30)
  }

  if (averageMs <= WARNING_MIN_MS) {
    // OPTIMAL_MAX_MS..WARNING_MIN_MS -> 70..35
    const t = (averageMs - OPTIMAL_MAX_MS) / (WARNING_MIN_MS - OPTIMAL_MAX_MS)
    return Math.round(70 - t * 35)
  }

  if (averageMs <= POOR_MIN_MS) {
    // WARNING_MIN_MS..POOR_MIN_MS -> 35..15
    const t = (averageMs - WARNING_MIN_MS) / (POOR_MIN_MS - WARNING_MIN_MS)
    return Math.round(35 - t * 20)
  }

  // POOR_MIN_MS..ZERO_PERCENT_CEILING_MS -> 15..0, clamped at 0 beyond the ceiling
  const t = Math.min((averageMs - POOR_MIN_MS) / (ZERO_PERCENT_CEILING_MS - POOR_MIN_MS), 1)
  return Math.round(15 - t * 15)
}

const applyLossPenalty = (quality) => {
  if (networkQualityState.lossRate >= LOSS_POOR_RATE) {
    return NETWORK_QUALITY.POOR
  }

  if (networkQualityState.lossRate >= LOSS_WARNING_RATE) {
    const index = TIER_ORDER.indexOf(quality)
    if (index >= 0 && index < TIER_ORDER.length - 1) {
      return TIER_ORDER[index + 1]
    }
  }

  return quality
}

const computeQualityFromSamples = () => {
  if (networkQualityState.samples.length === 0) {
    networkQualityState.quality = NETWORK_QUALITY.UNKNOWN
    networkQualityState.averageMs = null
    networkQualityState.qualityPercentage = null
    return
  }

  const average = networkQualityState.samples.reduce((sum, ms) => sum + ms, 0) / networkQualityState.samples.length
  networkQualityState.averageMs = Math.round(average)
  networkQualityState.qualityPercentage = computeQualityPercentage(average)
  networkQualityState.quality = applyLossPenalty(computeQualityLabel(networkQualityState.qualityPercentage))

  // Coherencia UI: si la pérdida degradó la etiqueta, el porcentaje mostrado se
  // capa al máximo de su tier final para que no se lea "Conexión deficiente
  // (95%)", contradiciendo el porcentaje que el AC ata al estado de advertencia.
  const tierMax = TIER_MAX_PERCENTAGE[networkQualityState.quality]
  if (tierMax !== undefined) {
    networkQualityState.qualityPercentage = Math.min(networkQualityState.qualityPercentage, tierMax)
  }
}

const recordResult = (ok) => {
  if (isCurrentlyOffline()) {
    return
  }

  networkQualityState.results.push(ok)
  networkQualityState.lastSampleAt = Date.now()

  if (networkQualityState.results.length > LOSS_WINDOW) {
    networkQualityState.results.shift()
  }

  const total = networkQualityState.results.length
  networkQualityState.lossRate = total === 0 ? 0 : networkQualityState.results.filter((result) => !result).length / total

  // La pérdida debe degradar la calidad de inmediato (la siguiente muestra de
  // latencia puede tardar hasta 30s en llegar).
  if (networkQualityState.samples.length > 0) {
    computeQualityFromSamples()
  } else if (networkQualityState.lossRate >= LOSS_WARNING_RATE) {
    // Sondeo fallando desde el arranque: sin muestras de latencia no hay
    // porcentaje, pero la pérdida por sí sola tiene que ser visible. Un UNKNOWN
    // dejaría el indicador oculto justo cuando más se necesita la alerta.
    networkQualityState.quality = networkQualityState.lossRate >= LOSS_POOR_RATE
      ? NETWORK_QUALITY.POOR
      : NETWORK_QUALITY.WARNING
    networkQualityState.averageMs = null
    networkQualityState.qualityPercentage = null
  }
}

export const recordLatencySample = (durationMs) => {
  if (isCurrentlyOffline()) {
    return
  }

  networkQualityState.samples.push(durationMs)
  networkQualityState.lastSampleAt = Date.now()

  if (networkQualityState.samples.length > MAX_SAMPLES) {
    networkQualityState.samples.shift()
  }

  computeQualityFromSamples()
}

export const setOffline = () => {
  networkQualityState.isOffline = true
  networkQualityState.results = []
  networkQualityState.quality = NETWORK_QUALITY.OFFLINE
  networkQualityState.qualityPercentage = null
  networkQualityState.averageMs = null
}

export const clearOfflineIfOnline = () => {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) {
    networkQualityState.isOffline = false
    computeQualityFromSamples()
  }
}

// Un error a nivel de red (timeout, conexión caída, DNS) no tiene
// `error.response`. Los errores HTTP (4xx/5xx) son del servidor, no pérdida.
const isNetworkError = (error) => !!(error && error.config && !error.response)

// Para el sondeo cuenta como pérdida solo lo que aísla un problema de red o
// del servicio: error de red (sin response), 5xx (servicio degradado/config
// rota) y 408 (timeout del servidor). Los 4xx NO: un 401/403 es un problema de
// autenticación (sesión expirada o rol sin permiso) y un 429 es throttling del
// backend; contarlos como pérdida fabrica un falso alerta de red con una red
// perfecta. Esos status se tratan como "sin dato": no entran como éxito ni
// como pérdida y el indicador conserva su último valor medido.
const isProbeLoss = (error) => {
  if (isNetworkError(error)) {
    return true
  }

  const status = error && error.response && error.response.status
  return status >= 500 || status === 408
}

export const attachNetworkQualityInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    config.metadata = { startedAt: performance.now() }
    return config
  })

  axiosInstance.interceptors.response.use(
    (response) => {
      if (response.config && response.config.metadata) {
        // El tiempo de las peticiones de negocio incluye consultas pesadas del
        // servidor: no se usa para medir red (solo cuenta como éxito/pérdida).
        recordResult(true)
      }
      return response
    },
    (error) => {
      if (isNetworkError(error)) {
        recordResult(false)
      }
      return Promise.reject(error)
    }
  )
}

export const attachProbeLatencyInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    config.metadata = { startedAt: performance.now() }
    return config
  })

  axiosInstance.interceptors.response.use(
    (response) => {
      if (response.config && response.config.metadata) {
        // El sondeo es un endpoint mínimo: su tiempo de ida y vuelta es
        // esencialmente latencia de red, sin tiempo de negocio del servidor.
        recordResult(true)
        recordLatencySample(performance.now() - response.config.metadata.startedAt)
      }
      return response
    },
    (error) => {
      // El sondeo es un endpoint mínimo: un fallo de red, 5xx o 408 ahí
      // significa red o servicio degradado y cuenta como paquete perdido
      // (isProbeLoss). Un 4xx (auth/throttle) se trata como "sin dato" para no
      // inventar una alerta de red cuando la sesión murió o el backend limita.
      if (isProbeLoss(error)) {
        recordResult(false)
      }
      return Promise.reject(error)
    }
  )
}
